'use client';

import { FirebaseError } from 'firebase/app';
import type { User } from 'firebase/auth';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cookie from 'react-cookies';

import { useTranslation } from '@/i18n/client';
import {
  logout as LogoutService,
  getUserMindProfile,
  login,
  smsLogin,
  updateUserProfile as updateUserProfileService
} from '@/service/login';
import { LoginErrorType } from '@/types/auth-model';
import { EGlobalLocalStorageKey } from '@/types/common';
import { EGlobalCookieKey } from '@/types/cookie';
import type {
  IProfileUserDetail,
  IProfileUserUpdateUserDetail,
  ISmsLoginRequest,
  LoginResponse
} from '@/types/login';
import type { NewMindDetail } from '@/types/mindDetail';
import { EVENT } from '@/utils/event';
import {
  forgetPassword,
  loginMindOSWithFirebaseUser,
  loginWithApple,
  loginWithEmail,
  loginWithGoogle, // setupUserId,
  signUpWithEmail
} from '@/utils/firebase';
import reportClick, { EReportClickKey } from '@/utils/report/reportClick';
import { ROUTER_PATH, generateParams } from '@/utils/router';

const UserTokenKey = EGlobalCookieKey.token;
const UserEncryptKey = EGlobalCookieKey.encrypt_key;
const USER_PROFILE_KEY = 'user_profile';

const userIdKey = EGlobalLocalStorageKey.userId;

export default function useAuthModel() {
  const [isLogin, setIsLogin] = useState<boolean | undefined>(); // 增加一种状态，undefined 说明鉴权请求还没完成，有些内容不做展示。
  const [userProfile, setUserProfile] = useState<IProfileUserDetail>(); // 用户信息（用于展示）
  const [mindDetail, setMindDetail] = useState<NewMindDetail | null>(null); // 用户标识（用于业务数据）
  const [isNewUser, setIsNewUser] = useState<LoginResponse['isNewUser']>();
  const router = useRouter();
  const { t } = useTranslation();

  const LOCAL_STORAGE_KEYS = useMemo(
    () => [
      {
        key: USER_PROFILE_KEY,
        set: setUserProfile
      }
    ],
    []
  );

  const handleLoginSuccess = (data: LoginResponse) => {
    setIsNewUser(data.isNewUser);
    sessionStorage.setItem('hasPA', data.hasPa);
    sessionStorage.setItem('needLiteOnboarding', String(data.needLiteOnboarding));
    sessionStorage.setItem('toBeDeleted', String(data.toBeDeleted));

    const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');
    const inviteCode = localStorage.getItem('inviteCode');
    const circleId = localStorage.getItem('circleId');
    const talkId = localStorage.getItem('talkId');
    const talkInviteCode = localStorage.getItem('talkInviteCode');
    const snapId = localStorage.getItem('snapId');
    const snapInviteCode = localStorage.getItem('snapInviteCode');
    const loginType = localStorage.getItem('loginType');

    // Determine invite type and id for reporting
    const getInviteTypeAndId = (): { type: string; id: string } => {
      if (inviteCode) return { type: 'inviteCode', id: inviteCode };

      if (circleId) return { type: 'circleId', id: circleId };

      if (talkId) return { type: 'talkId', id: talkId };

      if (talkInviteCode) return { type: 'talkInviteCode', id: talkInviteCode };

      if (snapId) return { type: 'snapId', id: snapId };

      if (snapInviteCode) return { type: 'snapInviteCode', id: snapInviteCode };

      return { type: '', id: '' };
    };

    const { type: inviteType, id: inviteId } = getInviteTypeAndId();

    reportClick(EReportClickKey.login_success, {
      key1: data.isNewUser,
      key2: loginType ?? '',
      key5: visitHomepageUserId ?? '',
      key6: inviteCode ?? '',
      key7: circleId ?? '',
      key8: inviteType,
      key9: inviteId
    });
    localStorage.removeItem('homepageUserId');
    sessionStorage.removeItem('visitHomepageUserId');
    localStorage.removeItem('inviteCode');
    localStorage.removeItem('circleId');
    localStorage.removeItem('talkId');
    localStorage.removeItem('talkOutputMode');
    localStorage.removeItem('talkInviteCode');
    localStorage.removeItem('snapId');
    localStorage.removeItem('snapInviteCode');
    localStorage.removeItem('loginType');

    // 可能需要针对于用户信息进行采集
    saveCookie(UserTokenKey, data.token ?? '');
    saveCookie(UserEncryptKey, data.key);
    cookie.save(EGlobalCookieKey.wide_token, data.token ?? '', {
      path: '/',
      maxAge: 86400 * 30,
      domain: '.mindos.com'
    });
    cookie.save(EGlobalCookieKey.wide_token, data.token ?? '', {
      path: '/',
      maxAge: 86400 * 30,
      domain: '.me.bot'
    });
  };

  const clearUserLoginState = useCallback(() => {
    cookie.remove(EGlobalCookieKey.wide_token, { domain: '.mindos.com' });
    setMindDetail(null);
    removeCookie(UserTokenKey);
    removeCookie(UserEncryptKey);
    setIsLogin(false);
    setIsNewUser(undefined);
    localStorage.removeItem(userIdKey);
    localStorage.removeItem('hasFinishedGuide3Notes');
    dispatchEvent(new Event(EVENT.LOG_OUT));

    LOCAL_STORAGE_KEYS.forEach((item) => {
      removeCookie(item.key);
      item.set(undefined);
    });
  }, [LOCAL_STORAGE_KEYS]);

  useEffect(() => {
    const token = cookie.load(UserTokenKey);

    if (token) {
      setIsLogin(true);
      loadCache();
      // 启动后刷新
      loadUserDetail();
    } else {
      clearUserLoginState();
    }
  }, []);

  useEffect(() => {
    function cb() {
      clearUserLoginState();
      // useModel内部不能用useSearchParams 会打包报错
      window.location.href.includes('?redirect=')
        ? null
        : router.replace(
            generateParams(ROUTER_PATH.login, {
              redirect: encodeURIComponent(window.location.href)
            })
          );
    }

    addEventListener(EVENT.TOKEN_EXPIRED, cb);

    return () => {
      removeEventListener(EVENT.TOKEN_EXPIRED, cb);
    };
  }, [router, clearUserLoginState]);

  // useEffect(() => {
  //   const userId = isLogin ? userProfile?.userId : undefined;

  //   setupUserId(userId ?? 'anonymous');
  // }, [isLogin, userProfile]);

  const saveCookie = (key: string, value: string | number | object) => {
    // TODO 临时延长
    cookie.save(key, value, { path: '/', maxAge: 86400 * 30 });
  };

  const removeCookie = (key: string) => {
    cookie.remove(key, { path: '/' });
  };

  const loadCache = () => {
    LOCAL_STORAGE_KEYS.forEach((item) => {
      const value = cookie.load(item.key);

      if (value) {
        item.set(value);
      }
    });
  };

  const loginWithGmail = () => {
    return loginWithGoogle().then((user) => {
      return loginMindOSWithFirebaseUser(user)
        .then((res) => {
          if (res.data.code !== 0) {
            throw Error(res.data.message ?? 'Login failed');
          }

          handleLoginSuccess(res.data.data);
        })
        .then(() => {
          setIsLogin(true);

          return loadUserDetail();
        })
        .catch((error) => {
          clearUserLoginState();

          throw formatError(error);
        });
    });
  };

  const loginWithUser = (user: User) => {
    return loginMindOSWithFirebaseUser(user)
      .then((res) => {
        if (res.data.code !== 0) {
          throw Error(res.data.message ?? 'Login failed');
        }

        handleLoginSuccess(res.data.data);
      })
      .then(() => {
        setIsLogin(true);

        return loadUserDetail();
      })
      .catch((error) => {
        clearUserLoginState();

        throw formatError(error);
      });
  };

  const loginWithAppleFunc = () => {
    return loginWithApple().then((user) => {
      return loginMindOSWithFirebaseUser(user)
        .then((res) => {
          if (res.data.code !== 0) {
            throw Error(res.data.message ?? 'Login failed');
          }

          handleLoginSuccess(res.data.data);
        })
        .then(() => {
          setIsLogin(true);

          return loadUserDetail();
        })
        .catch((error) => {
          clearUserLoginState();

          throw formatError(error);
        });
    });
  };

  const loadUserDetail = () => {
    return Promise.all([loadUserProfile()]);
  };

  const loginWithFirebase = (email: string, password: string) => {
    return loginWithEmail(email, password)
      .then((user) => {
        if (!user.emailVerified) {
          throw Error(LoginErrorType.VerifyFailed);
        }

        return loginMindOSWithFirebaseUser(user);
      })
      .then((res) => {
        if (res.data.code !== 0) {
          throw Error(res.data.message ?? 'Login failed');
        }

        handleLoginSuccess(res.data.data);
      })
      .then(() => {
        setIsLogin(true);

        return loadUserDetail();
      })
      .catch((error) => {
        clearUserLoginState();

        throw formatError(error);
      });
  };

  const loginWithSms = (data: ISmsLoginRequest) => {
    const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');

    reportClick(EReportClickKey.login, { key1: 'sms', key5: visitHomepageUserId ?? '' });
    localStorage.setItem('loginType', 'sms');

    return smsLogin(data)
      .then((res) => {
        if (res.data.code !== 0) {
          throw Error(res.data.message ?? 'Login failed');
        }

        handleLoginSuccess(res.data.data);
      })
      .then(() => {
        setIsLogin(true);

        return loadUserDetail();
      })
      .catch((error) => {
        reportClick(EReportClickKey.login_error, {
          key1: 'sms',
          key2: error.code ?? '',
          key3: error.message ?? ''
        });
        clearUserLoginState();

        throw formatError(error);
      });
  };

  const loginWithMindOS = (username: string, password: string) => {
    return login({ username, password })
      .then((res) => {
        if (res.data.code !== 0) {
          throw Error(res.data.message ?? 'Login failed');
        }

        handleLoginSuccess(res.data.data);
      })
      .then(() => {
        setIsLogin(true);

        return loadUserDetail();
      })
      .catch((error) => {
        clearUserLoginState();

        throw formatError(error);
      });
  };

  const signUp = (email: string, password: string) => {
    return signUpWithEmail(email, password);
  };

  const checkIsLogin = () => {
    return cookie.load(UserTokenKey) !== undefined;
  };

  const logout = () => {
    return LogoutService().then(() => {
      const params = new URLSearchParams(window.location.search);

      params.set('isLogout', 'true');

      const newUrl = `${window.location.pathname}?${params.toString()}`;

      window.history.replaceState({ isLogout: 'true' }, '', newUrl);

      clearUserLoginState();

      setTimeout(() => {
        router.replace(ROUTER_PATH.login);
      }, 100);
    });
  };

  const loadUserProfile = () => {
    return new Promise<void>((resolve, reject) => {
      getUserMindProfile().then((res) => {
        if (res.data.code !== 0) {
          reject(res.data.message ?? 'Login failed');
        } else {
          const user = res.data.data.operatorProfileInfo;

          setUserProfile({
            appearance: user.appearance ?? {
              url: '',
              description: '',
              rejectionReason: '',
              verified: false
            },
            avatar: user.avatar ?? '',
            aboutMe: user.aboutMe ?? '',
            email: user.email ?? '',
            name: user.name ?? '',
            userId: user.userId ?? '',
            route: user.route ?? '',
            homepageStatus: user.homepageStatus,
            homepage: user.homepage
          });
          setMindDetail(res.data.data.mindDetail);
          localStorage.setItem(userIdKey, user.userId ?? '');
          resolve();
        }
      });
    });
  };

  const updateUserProfile = (userDetailModel: IProfileUserUpdateUserDetail) => {
    return new Promise<void>((resolve, reject) => {
      updateUserProfileService(userDetailModel)
        .then((res) => {
          if (res.data.code !== 0) {
            reject(new Error(res.data.message ?? 'update failed'));
          } else {
            const newProfile = {
              email: userProfile?.email ?? '',
              aboutMe: userDetailModel.aboutMe
                ? userDetailModel.aboutMe
                : userProfile?.aboutMe ?? '',
              avatar: userDetailModel.avatar ? userDetailModel.avatar : userProfile?.avatar ?? '',
              name: userDetailModel.name ? userDetailModel.name : userProfile?.name ?? '',
              userId: userDetailModel.userId || userProfile?.userId || '',
              route: userDetailModel.route || userProfile?.route || '',
              homepageStatus: userDetailModel.homepageStatus || userProfile?.homepageStatus,
              homepage: userDetailModel.homepage || userProfile?.homepage,
              appearance: userDetailModel.appearance
                ? userDetailModel.appearance
                : userProfile?.appearance ?? {
                    url: '',
                    description: '',
                    rejectionReason: '',
                    verified: false
                  }
            };

            setUserProfile(newProfile);
            localStorage.setItem(userIdKey, newProfile.userId);
            resolve();
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const resetPassword = () => {
    return new Promise<void>((resolve, reject) => {
      const email = userProfile?.email ?? '';

      if (email.includes('@')) {
        forgetPassword(email)
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        reject(new Error('email not valid'));
      }
    });
  };

  const formatError = (error: Error) => {
    if (error instanceof FirebaseError) {
      const newError: FirebaseError = error;
      const code = newError.code;

      switch (code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          return new Error(t('login.error.wrongEmailOrPassword'));
        case 'auth/popup-blocked':
          return new Error(t('login.error.popupBlocked'));
        case 'auth/too-many-requests':
          return new Error(t('login.error.tooManyRequests'));
        default:
          return new Error(t('login.error.unexpectedProblem'));
      }
    } else {
      return error;
    }
  };

  return {
    isLogin,
    isNewUser,
    loginWithGmail,
    loginWithApple: loginWithAppleFunc,
    loginWithFirebase,
    loginWithUser,
    loginWithMindOS,
    loginWithSms,
    logout,
    signUp,
    checkIsLogin,
    userProfile,
    updateUserProfile,
    resetPassword,
    mindDetail,
    loadUserDetail
  };
}
